import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    BENEFICIARY_TYPE_OPTIONS,
    type CreateExpensePayload,
    type Expense,
    type ExpenseSummary,
    type SelectOption,
    type UpdateExpensePayload,
} from '../types/LegalCase';
import {
    EXPENSE_FILTER_DEFS,
    buildExpenseFilterDefs,
} from '../components/pages/recovery/shared/expenseFilterTypes';
import {
    emptyFilterValues,
    toQueryParams,
    type FilterValues,
} from '../components/pages/recovery/shared/filterTypes';
import type { ErrorField } from '../types/ErrorField';

const EMPTY_SUMMARY: ExpenseSummary = {
    total: 0,
    totalFormatted: null,
    count: 0,
    byCategory: [],
};

const CATEGORY_FALLBACK: SelectOption[] = [
    { value: 'COURT_COSTS', label: 'Custas judiciais' },
    { value: 'LITIGATION_FEES', label: 'Honorários advocatícios contencioso' },
    { value: 'ADVISORY_FEES', label: 'Honorários advocatícios consultivo' },
    { value: 'REGISTRATION', label: 'Despesas de registro' },
    { value: 'COLLATERAL_MONITORING', label: 'Monitoramento de garantias' },
    { value: 'CREDIT_ACQUISITION_RTD_RI', label: 'Aquisição de créditos para RTD e RI' },
    { value: 'CERTIFICATE_ISSUANCE', label: 'Emissão de certidões' },
    { value: 'NOTARIAL_DEED', label: 'Ata notarial' },
    { value: 'APPRAISAL_TRANSPORT', label: 'Avaliação/transportes de maquinários' },
    { value: 'INPI', label: 'INPI' },
    { value: 'OTHER', label: 'Outro' },
];

// Fallback dos tipos de beneficiário: importado de types/LegalCase (fonte única no frontend),
// não redeclarado aqui — ver o comentário em BENEFICIARY_TYPE_OPTIONS.
const BENEFICIARY_TYPE_FALLBACK: SelectOption[] = BENEFICIARY_TYPE_OPTIONS;

// Gerencia a página top-level de Despesas: lista geral (com o agregado calculado total +
// quebra por categoria — o rodapé da planilha do jurídico), busca + filtros em drawer
// (categoria, com/sem processo, beneficiário) no mesmo modelo data-driven da grid de Casos,
// cadastro (com caso e beneficiário opcionais) e remoção. Também carrega as opções de
// categoria e tipo de beneficiário do page-form, com fallback local.
export default function useExpensesPage() {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [summary, setSummary] = useState<ExpenseSummary>(EMPTY_SUMMARY);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [search, setSearch] = useState('');
    const [filterValues, setFilterValues] = useState<FilterValues>(() =>
        emptyFilterValues(EXPENSE_FILTER_DEFS),
    );
    const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>(CATEGORY_FALLBACK);
    const [beneficiaryTypeOptions, setBeneficiaryTypeOptions] =
        useState<SelectOption[]>(BENEFICIARY_TYPE_FALLBACK);

    // Os parâmetros de filtro saem das defs (mesmo mecanismo da grid de Casos). Serializados
    // numa string estável para a dependência do efeito, evitando refetch redundante.
    const filterParams = toQueryParams(EXPENSE_FILTER_DEFS, filterValues);
    const filterKey = JSON.stringify(filterParams);

    const fetchExpenses = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await recoveryApi.getExpenses({
                search: search || undefined,
                ...toQueryParams(EXPENSE_FILTER_DEFS, filterValues),
            });
            setExpenses(result.items || []);
            setSummary(result.summary || EMPTY_SUMMARY);
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao carregar despesas');
        } finally {
            setIsLoading(false);
        }
        // filterValues entra via filterKey (string estável); incluí-lo direto refetchava à toa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filterKey]);

    useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

    useEffect(() => {
        recoveryApi.getLegalCaseFormOptions().then((opts) => {
            if (!opts) return;
            if (opts.expenseCategories?.length) setCategoryOptions(opts.expenseCategories);
            if (opts.beneficiaryTypes?.length) setBeneficiaryTypeOptions(opts.beneficiaryTypes);
        }).catch(() => { /* fallback local */ });
    }, []);

    const setFilterValue = (id: string, value: string | boolean) => {
        setFilterValues((prev) => ({ ...prev, [id]: value }));
    };

    const clearFilters = () => {
        setFilterValues(emptyFilterValues(EXPENSE_FILTER_DEFS));
    };

    const clearErrors = () => setErrorFields([]);

    const addExpense = async (data: CreateExpensePayload): Promise<boolean> => {
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.createExpense(data);
            await fetchExpenses();
            toast.success('Despesa registrada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao registrar despesa');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateExpense = async (
        uuid: string,
        data: UpdateExpensePayload,
    ): Promise<boolean> => {
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateExpense(uuid, data);
            await fetchExpenses();
            toast.success('Despesa atualizada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar despesa');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeExpense = async (uuid: string) => {
        setIsSaving(true);
        try {
            await recoveryApi.removeExpense(uuid);
            await fetchExpenses();
            toast.success('Despesa removida');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover despesa');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        expenses, summary, isLoading, isSaving, errorFields,
        search, setSearch,
        filterValues, setFilterValue, clearFilters,
        categoryOptions, beneficiaryTypeOptions,
        addExpense, updateExpense, removeExpense, clearErrors, refresh: fetchExpenses,
    };
}
